import { type RaffleWinnersOrganizerResDto } from '@web/common';
import {
  showErrorNotification,
  showSuccessNotification,
} from '../../../../component/notification';
import {
  getRaffleWinnersForOrganizer,
  markRafflePrizeCollected,
} from '../../../../request/authenticated-requests/raffle';
import { setupMobX } from '../../../../util/setupMobX';
import {
  observable,
  makeObservable,
  action,
  runInAction,
  computed,
} from 'mobx';

export type RaffleWinner = RaffleWinnersOrganizerResDto[number];

export class RaffleWinnersStore {
  @observable public isLoading: boolean = false;
  @observable public winners: RaffleWinnersOrganizerResDto = [];
  @observable public showCollected: boolean = true;
  @observable public selectedWinner: RaffleWinner | null = null;

  constructor() {
    makeObservable(this);
  }

  @computed public get filteredWinners(): RaffleWinnersOrganizerResDto {
    if (this.showCollected) {
      return this.winners;
    }
    return this.winners.filter((winner) => winner.collectedAt === null);
  }

  @action public loadWinners = () => {
    this.isLoading = true;
    getRaffleWinnersForOrganizer()
      .then((winners) => runInAction(() => (this.winners = winners)))
      .catch(() => showErrorNotification())
      .finally(() => runInAction(() => (this.isLoading = false)));
  };

  @action public toggleShowCollected = () => {
    this.showCollected = !this.showCollected;
  };

  @action public selectWinner = (winner: RaffleWinner) => {
    this.selectedWinner = winner;
  };

  @action public clearSelectedWinner = () => {
    this.selectedWinner = null;
  };

  @action public confirmCollection = async (successMessage: string) => {
    if (!this.selectedWinner) return;

    const { raffleId, userId } = this.selectedWinner;
    try {
      const response = await markRafflePrizeCollected(raffleId, userId);
      runInAction(() => {
        const winner = this.winners.find(
          (w) => w.raffleId === raffleId && w.userId === userId,
        );
        if (winner) {
          winner.collectedAt = response.collectedAt;
        }
        this.selectedWinner = null;
      });
      showSuccessNotification(successMessage);
    } catch {
      showErrorNotification();
    }
  };
}

const { provider, useStore } = setupMobX<RaffleWinnersStore>();
export const useRaffleWinnersStore = useStore;
export const RaffleWinnersStoreProvider = provider;
